import { Component, OnInit, inject } from '@angular/core';
import { RouterLink } from '@angular/router';
import { MapComponent, MapMarker } from '../../shared/components/map/map.component';
import { ScrollAnimateDirective } from '../../shared/directives/scroll-animate.directive';
import { SeoService } from '../../core/services/seo.service';
import { SiteConfigService } from '../../core/config/site-config.service';
import { ParticlesComponent } from '../../shared/components/particles/particles.component';
@Component({
  selector: 'app-divisions',
  standalone: true,
  imports: [RouterLink, MapComponent, ScrollAnimateDirective, ParticlesComponent],
  template: `
    <!-- Hero -->
    <section class="relative py-24 md:py-32 -mt-20 pt-32 md:pt-40 bg-surface-950 overflow-hidden grain-overlay">
      <div class="absolute inset-0 bg-gradient-to-br from-brand-950 via-surface-950 to-brand-900"></div>
      <app-particles class="absolute inset-0 z-[1] pointer-events-none" [count]="50" [connectionDistance]="130" [mouseRadius]="180" />
      <div class="mx-auto max-w-7xl px-6 lg:px-8 relative z-10">
        <div class="max-w-3xl">
          <div class="section-label section-label-light">{{ page.hero.label }}</div>
          <h1 class="font-heading text-4xl md:text-5xl lg:text-6xl font-extrabold text-white leading-[1.08] tracking-[-0.03em] mb-6">
            {{ page.hero.title }} <span class="text-brand-300">{{ page.hero.highlight }}</span>
          </h1>
          <p class="text-lg text-surface-400 leading-relaxed max-w-xl">{{ page.hero.description }}</p>
        </div>
      </div>
    </section>

    <!-- Divisions Grid -->
    <section class="py-24 md:py-32 bg-white">
      <div class="mx-auto max-w-7xl px-6 lg:px-8">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          @for (div of page.divisions; track div.name; let i = $index) {
            <div appScrollAnimate [attr.data-animate]="''" [attr.data-delay]="(i % 2) * 100 + 100"
                 class="group rounded-2xl border border-surface-100 bg-white overflow-hidden transition-all duration-300 hover:shadow-xl hover:shadow-surface-900/[0.06] hover:border-surface-200">
              <!-- Image -->
              @if (div.image) {
                <div class="relative aspect-[16/9] overflow-hidden">
                  <img [src]="div.image" [alt]="div.name"
                       class="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <div class="absolute inset-0 bg-gradient-to-t from-surface-950/60 via-transparent to-transparent"></div>
                  <div class="absolute bottom-4 left-5 flex items-center gap-3">
                    <div class="w-10 h-10 rounded-lg flex items-center justify-center backdrop-blur-sm bg-white/15" [style.background]="div.bgColor">
                      <span class="text-xl" [innerHTML]="div.icon"></span>
                    </div>
                    <h3 class="font-heading text-lg font-bold text-white drop-shadow-md">{{ div.name }}</h3>
                  </div>
                </div>
              } @else {
                <div class="flex items-start gap-5 p-8 pb-0 mb-6">
                  <div class="w-14 h-14 rounded-xl flex items-center justify-center shrink-0" [style.background]="div.bgColor">
                    <span class="text-2xl" [innerHTML]="div.icon"></span>
                  </div>
                  <div>
                    <h3 class="font-heading text-xl font-bold text-surface-900 mb-1 group-hover:text-brand-600 transition-colors">{{ div.name }}</h3>
                  </div>
                </div>
              }

              <div class="p-6 pt-5">
                <p class="text-sm text-surface-500 leading-relaxed mb-5">{{ div.desc }}</p>

                <!-- Capabilities -->
                <div class="grid grid-cols-2 gap-2.5 mb-5">
                  @for (cap of div.capabilities; track cap) {
                    <div class="flex items-center gap-2 text-sm text-surface-600">
                      <div class="w-1.5 h-1.5 rounded-full bg-brand-500 shrink-0"></div>
                      {{ cap }}
                    </div>
                  }
                </div>

                <!-- Stats -->
                <div class="flex items-center gap-6 pt-4 border-t border-surface-100">
                  <div>
                    <span class="font-heading text-xl font-bold text-surface-900">{{ div.projects }}+</span>
                    <span class="text-xs text-surface-400 ml-1">Projects</span>
                  </div>
                  <div>
                    <span class="font-heading text-xl font-bold text-surface-900">{{ div.team }}</span>
                    <span class="text-xs text-surface-400 ml-1">Specialists</span>
                  </div>
                  @if (div.url) {
                    <a [href]="div.url" class="ml-auto text-sm font-semibold text-brand-600 hover:text-brand-700 transition-colors">
                      Visit Site →
                    </a>
                  }
                </div>
              </div>
            </div>
          }
        </div>
      </div>
    </section>

    <!-- Map -->
    <section class="py-20 md:py-28 bg-surface-50">
      <div class="mx-auto max-w-7xl px-6 lg:px-8">
        <div class="text-center mb-12" appScrollAnimate data-animate>
          <div class="section-label justify-center">Service Area</div>
          <h2 class="font-heading text-3xl md:text-4xl font-bold text-surface-900 tracking-[-0.025em] mb-4">
            Serving South Florida
          </h2>
          <p class="text-surface-500 max-w-2xl mx-auto">
            All four divisions serve Miami-Dade, Broward, and Palm Beach counties from our central location.
          </p>
        </div>
        <div appScrollAnimate data-animate class="h-[400px] rounded-xl overflow-hidden border border-surface-200 shadow-sm">
          <app-map [lat]="contact.coordinates.lat" [lng]="contact.coordinates.lng" [zoom]="10" [markers]="mapMarkers" />
        </div>
      </div>
    </section>

    <!-- CTA -->
    <section class="py-20 bg-brand-600">
      <div class="mx-auto max-w-4xl px-6 lg:px-8 text-center">
        <h2 class="font-heading text-3xl md:text-4xl font-bold text-white mb-6">
          Find the Right Division for Your Project
        </h2>
        <p class="text-brand-100 mb-8 max-w-lg mx-auto">
          Not sure which division fits your needs? Our team will connect you with the right specialists.
        </p>
        <a routerLink="/contact" class="inline-flex items-center gap-2.5 px-8 py-4 bg-white text-brand-700 font-semibold text-sm rounded-lg transition-all duration-300 hover:bg-brand-50 hover:shadow-lg">
          Contact Us
          <svg aria-hidden="true" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
        </a>
      </div>
    </section>
  `,
})
export class DivisionsComponent implements OnInit {
  private seo = inject(SeoService);
  private config = inject(SiteConfigService);

  page = this.config.pages.divisions!;
  contact = this.config.contact;
  brand = this.config.brand;

  mapMarkers: MapMarker[] = [
    { lat: this.contact.coordinates.lat, lng: this.contact.coordinates.lng, label: this.brand.name, description: this.contact.address.full },
  ];

  ngOnInit(): void {
    this.seo.setJsonLd(this.seo.getBreadcrumbSchema([
      { name: 'Home', url: '/' },
      { name: 'Divisions', url: '/divisions' },
    ]));
  }
}
